import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import {
  AlertTriangle, CheckCircle2, ChevronRight,
  Building2, Receipt, Percent, Briefcase, Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FLAG_META } from "@/components/flags/SmartFlags";

/**
 * FlagsPreflightCard — "N issues to review" chip above the Share row on
 * every accountant-pack surface (main /tax pack, BAS, TPAR, Personal Tax).
 * Advisory only — never blocks Share, but gives the tradie a chance to
 * fix subcontractor ABNs / missing receipts / GST mismatches / unassigned
 * jobs BEFORE the accountant sees them.
 *
 * Props:
 *   fy     : optional FY (backend defaults to current if omitted)
 *   scope  : optional array of flag keys to surface — e.g. ["missing_abn_subcontractor"]
 *            for the TPAR panel. Defaults to all keys (used on the main pack row).
 *   title  : optional label override for the collapsed heading — defaults
 *            to "N issue(s) to review before you send".
 *   testid : optional test id override for parallel surfaces.
 */
const KEY_ICON = {
  missing_abn_subcontractor: Building2,
  no_receipt: Receipt,
  gst_mismatch: Percent,
  unassigned_job: Briefcase,
};

export default function FlagsPreflightCard({ fy, scope, title, testid }) {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = fy ? `?fy=${encodeURIComponent(fy)}` : "";
        const { data } = await api.get(`/flags/summary${params}`);
        if (!cancelled) setSummary(data);
      } catch (err) {
        devLog.warn("flags summary fetch failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fy]);

  // Filter to the pack's scope. When `scope` is omitted, show everything
  // — that's the main Accountant Pack behaviour on /tax.
  const { total, byKey, flags } = useMemo(() => {
    const allFlags = summary?.flags || [];
    const allByKey = summary?.by_key || {};
    if (!scope || !scope.length) {
      return { total: summary?.total || 0, byKey: allByKey, flags: allFlags };
    }
    const set = new Set(scope);
    const filtered = allFlags.filter((f) => set.has(f.key));
    // Server returns a truncated `flags[]` (first 50) — trust `by_key` for
    // totals so we don't undercount packs with more than 50 flagged rows.
    const scopedByKey = Object.fromEntries(
      Object.entries(allByKey).filter(([k]) => set.has(k))
    );
    const scopedTotal = Object.values(scopedByKey).reduce((a, b) => a + b, 0);
    return { total: scopedTotal, byKey: scopedByKey, flags: filtered };
  }, [summary, scope]);

  const rootTestId = testid || "flags-preflight-card";

  if (loading) {
    return (
      <div
        className="bi-surface p-3 flex items-center gap-2 text-[11px] text-white/40"
        data-testid={`${rootTestId}-loading`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking your pack…
      </div>
    );
  }

  if (total === 0) {
    return (
      <div
        data-testid={`${rootTestId}-clean`}
        className="bi-surface p-3 flex items-center gap-2 text-[12px] text-[#34D399]"
      >
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <span className="font-heading font-bold uppercase tracking-wider text-[11px]">
          Pack looks good — no open flags
        </span>
      </div>
    );
  }

  const heading = title
    ? title.replace("{n}", total).replace("{s}", total === 1 ? "" : "s")
    : `${total} issue${total === 1 ? "" : "s"} to review before you send`;

  return (
    <div
      data-testid={rootTestId}
      className="bi-surface p-3 border border-[#FBBF24]/25 bg-[#FBBF24]/[0.06]"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${rootTestId}-toggle`}
        className="w-full flex items-center gap-2 text-left"
      >
        <AlertTriangle className="w-4 h-4 text-[#FBBF24] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold uppercase tracking-wider text-[11px] text-[#FBBF24]">
            {heading}
          </div>
          <div className="text-[10px] text-white/50 truncate mt-0.5">
            {Object.entries(byKey).map(([k, n]) => `${n}× ${FLAG_META[k]?.label || k}`).join(" · ")}
          </div>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>

      {open && (
        <div className="mt-3 space-y-1.5" data-testid={`${rootTestId}-list`}>
          {flags.slice(0, 10).map((f, i) => {
            const meta = FLAG_META[f.key] || { label: f.key };
            const Icon = KEY_ICON[f.key] || AlertTriangle;
            return (
              <button
                key={`${f.entry_id}-${f.key}-${i}`}
                type="button"
                onClick={() => f.entry_id && nav(`/entries/${f.entry_id}/edit`)}
                data-testid={`${rootTestId}-row-${i}`}
                className="w-full flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-white/[0.04] text-left"
              >
                <Icon className="w-3.5 h-3.5 text-[#FBBF24] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-white/85 truncate">
                    <b>{meta.label}</b>
                    {f.supplier ? ` · ${f.supplier}` : ""}
                    {typeof f.amount === "number" ? ` · $${f.amount.toFixed(2)}` : ""}
                  </div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-white/30 shrink-0" />
              </button>
            );
          })}
          {flags.length > 10 && (
            <div className="text-[10px] text-white/40 pt-1">
              +{flags.length - 10} more — open the Ledger to filter by flag.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
